import React, {
	type ReactNode,
	type ReactElement,
	cloneElement,
	isValidElement,
	memo,
	useCallback,
	useMemo,
} from 'react';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import type { DroppableStateSnapshot } from 'react-beautiful-dnd';
import { ExperienceSuccess } from '@atlassian/jira-business-experience-tracking';
import { VIEW_EXPERIENCE } from '../../../../common/constants';
import type { BoardIssueOld } from '../../../../common/types';
import { useExpandVirtualCards } from '../../../../controllers/collapse-column-old';
import { useSelectedFields } from '../../../../controllers/fields-preference';
import { BoardVirtualizationProvider } from '../../../../controllers/virtualization-context-old';
import { useVirtualItems } from '../../../../controllers/virtualizer-old';
import { VirtualCardsContainer, VirtualCardWrapper } from './styled';
import { useReactBeautifulDnDElementHeight } from './utils';

type CardRenderer = (arg1: { issue: BoardIssueOld; cardIndex: number }) => ReactNode;

type Props = {
	issues: BoardIssueOld[];
	isClone?: boolean;
	isCollapsedColumn: boolean;
	cardRenderer: CardRenderer;
	scrollRef: {
		current: HTMLElement | null;
	};
	boardSize?: {
		width: number;
		height: number;
	};
	footer?: ReactElement | null;
	dndSnapshot: DroppableStateSnapshot;
};

export default memo<Props>(
	({
		scrollRef,
		boardSize,
		cardRenderer,
		issues,
		footer,
		dndSnapshot,
		isClone,
		isCollapsedColumn,
	}: Props) => {
		// re-renders VirtualCards to make sure cards height is re-calculated when the column is expanded
		const { isCollapsed } = useExpandVirtualCards(isCollapsedColumn);

		useSelectedFields();

		const useObserver = useCallback(() => boardSize, [boardSize]);
		const { virtualItems, totalSize, scrollToIndex, onBoardCardResize } = useVirtualItems({
			scrollRef,
			size: isCollapsed ? 0 : issues.length,
			overscan: 5,
			// @ts-expect-error undefined is not assignable to `useObserver`
			useObserver: isClone !== true ? useObserver : undefined,
			keyExtractor: (index) => issues[index].id,
		});

		const scrollToLastElement = useCallback(() => {
			scrollToIndex(issues.length, { align: 'start' });
		}, [scrollToIndex, issues.length]);

		// Take care when modifying this code. The scrollToLastElement prop must be
		// passed to the footer component as it is expected to be present at all
		// times in the underlying InlineCreate component.
		const scrollableFooter = useMemo(
			() =>
				footer !== undefined && footer !== null && isValidElement(footer)
					? // @ts-expect-error - TS2769 - No overload matches this call.
						cloneElement(footer, { scrollToLastElement })
					: footer,
			[scrollToLastElement, footer],
		);

		const cards = useMemo(
			() =>
				virtualItems
					.map((item) => ({
						issue: issues[item.index],
						cardIndex: item.index,
						positionY: item.start,
						onMeasureRef: item.measureRef,
						height: item.size,
						isDraggingCard:
							(dndSnapshot?.draggingFromThisWith || '').indexOf(String(issues[item.index]?.id)) >
							-1,
					}))
					.filter((item) => !!item.issue),
			[dndSnapshot?.draggingFromThisWith, issues, virtualItems],
		);

		const height = useReactBeautifulDnDElementHeight(dndSnapshot);

		if (isCollapsed) return null;

		return (
			<BoardVirtualizationProvider onBoardCardResize={onBoardCardResize}>
				<VirtualCardsContainer
					data-testid="work-management-board.ui.board-old.column-old.virtual-cards-old.virtual-cards-container"
					height={totalSize + height}
				>
					{cards.map((card) => (
						<VirtualCardWrapper
							key={card.issue.id}
							data-testid="work-management-board.ui.board-old.column-old.virtual-cards-old.virtual-cards-wrapper"
							// can't apply height and measure it at the same time - it leads to infinite renders
							ref={card.isDraggingCard ? undefined : card.onMeasureRef}
							transform={card.positionY}
						>
							{cardRenderer({
								issue: card.issue,
								cardIndex: card.cardIndex,
							})}
						</VirtualCardWrapper>
					))}
					<ExperienceSuccess experience={VIEW_EXPERIENCE} />
				</VirtualCardsContainer>
				{isClone !== true && scrollableFooter}
			</BoardVirtualizationProvider>
		);
	},
);
